import { usePostHog } from "posthog-js/react";
import type React from "react";

import { IconButton } from "@/components/Button/IconButton";
import { EyeOffIcon } from "@/components/Icons/EyeOffIcon";
import { XCloseIcon } from "@/components/Icons/XCloseIcon";
import { NobiGlob } from "@/components/NobiGlob/NobiGlob";
import { buttonVariants } from "@/design-system/Button";
import { Card } from "@/design-system/Card";
import { Inline } from "@/design-system/Inline";
import { twResolve } from "@/helpers/tw-resolve";
import { useTranslation } from "@/translations";

interface TicketLogNobiSuggestionCardProps {
  suggestion: string;
  onApply: () => void;
  onDismiss: () => void;
}

export function TicketLogNobiSuggestionCard({
  suggestion,
  onApply,
  onDismiss,
}: TicketLogNobiSuggestionCardProps): React.ReactNode {
  const { t } = useTranslation();
  const postHog = usePostHog();

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDismiss();

    postHog.capture("dismissed_nobi_admin_ticket_reply_suggestion", {
      source: "card",
    });
  };

  return (
    <Card as="article" density="flush" clip interactive>
      <button
        type="button"
        onClick={() => {
          postHog?.capture("applied_nobi_admin_ticket_reply_suggestion", {
            source: "card",
          });
          onApply();
        }}
        className={twResolve(
          buttonVariants({ variant: "soft" }),
          "flex h-fit w-full flex-col items-start gap-space-sm rounded-xl border-0 bg-background-default p-space-md pr-space-xl text-left whitespace-normal shadow-none hocus:bg-background-brand-tertiary-light/40",
        )}
      >
        <span className="flex gap-space-xs text-text-tertiary">
          <EyeOffIcon size={16} />
          <span className="text-caption">{t("common.label.only-visible-to-you")}</span>
        </span>
        <Inline gap="sm" align="center">
          <NobiGlob />
          <h4 className="text-body-emphasis leading-none text-text-brand-secondary">
            {t("common.label.nobi-suggestion")}
          </h4>
        </Inline>
        <p className="text-left text-body">{`"${suggestion}"`}</p>
      </button>
      <aside className="absolute top-space-sm right-space-sm">
        <IconButton
          icon={<XCloseIcon />}
          variant="ghostSecondary"
          size="xs"
          title={t("common.action.close")}
          withTooltip={false}
          onClick={handleDismiss}
        />
      </aside>
    </Card>
  );
}
